import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ProjectCard } from '../Models/ProjectCard';
import { MComment } from '../Models/MComment';
//saveComment, setComments
import { getProject } from '../Store/Project';
import { getSettings, setInsertUserInComment, setSearchText } from '../Store/Settings';
import { useAppDispatch, useAppSelector } from '../Core/hooks';
import { openSocket } from '../Helpers/openSocket';
import { store } from '../Core/store';
import UserMentionContextMenu from '../Modals/UserMentionContextMenu';
import { UserMentionContextMenuParams } from '../Models/Requests/UserMentionContextMenuParams';


/* Quill imports are required because nested lists in editor use classes, not
   true semmantic nested lists. To get them to display correctly in view mode, 
   we must import these and apply the same class Quill does in editor mode.
   https://github.com/quilljs/quill/issues/1314
*/
import 'react-quill/dist/quill.core.css';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.snow.css';

interface Props {
    mode: string;
    card: ProjectCard;
    comment?: MComment;
    closeHandler?: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void
}

function CardComment(props: Props) {
    const settingsState = useAppSelector(getSettings);
    const projectState = useAppSelector(getProject);
    const userSettings = useAppSelector(getSettings);
    const editorRef = useRef<ReactQuill | null>(null);
    const dispatch = useAppDispatch();
    const [richText, setRichText] = useState('');
    const [editing, setEditing] = useState<boolean>(false);
    const projectStateRef = useRef(projectState);
    const [userMentionContextMenuParams, setUserMentionContextMenuParams] = useState<UserMentionContextMenuParams>();
    const [userSearchText, setUserSearchText] = useState("");
    const userSearchTextRef = useRef(userSearchText);
    //let socket = openSocket(dispatch, settingsState);

    useEffect(() => {
        if (props.mode === "edit" && props.comment) {
            setEditing(true);
            setRichText(props.comment.richText);
        }
    }, []);

    useEffect(() => {
        userSearchTextRef.current = userSearchText;
      }, [userSearchText]);

    useEffect(() => {
        projectStateRef.current = projectState;
    }, [projectState]);

    useEffect(() => {
        if (editing && editorRef && editorRef.current && editorRef.current.editor) {
            let textLength = editorRef.current.editor.getLength();
            editorRef.current.editor.setSelection(textLength +1,1);
        }
    }, [editing])

    
    useEffect(() => {
        const quill = editorRef.current?.getEditor?.();
        if (!quill || settingsState.userInComment === undefined) return;
    
        const range = quill.getSelection(true);
        if (!range) return;
    
        const cursorIndex = range.index;
        const textBefore = quill.getText(0, cursorIndex);
        const match = textBefore.match(/@[\w\d]*$/); // match last @mention
    
        if (!match) return;
    
        const matchText = match[0]; // e.g., "@ke"
        const startIndex = cursorIndex - matchText.length;
    
        // Replace matched text with each selected user
        let insertIndex = startIndex;
        const fullMention = `@${settingsState.userInComment.firstName} ${settingsState.userInComment.lastName}`;
        quill.deleteText(insertIndex, matchText.length); // remove @ke
        quill.insertText(insertIndex, fullMention, { bold: true, italic: true }, 'user');
        insertIndex += fullMention.length;

        dispatch(setInsertUserInComment(undefined));
        // Defer formatting reset and cursor repositioning
        setTimeout(() => {
            quill.setSelection(insertIndex);
            quill.format('bold', false);
            quill.format('italic', false);
        }, 0);
    }, [settingsState.userInComment]);
    
    useEffect(() => {
        const interval = setInterval(() => {
            if (editorRef.current?.getEditor?.()) {
            // @ts-ignore
            editorRef.current.getEditor().getModule('toolbar').addHandler('image', () => {
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.click();
        
                input.onchange = () => {
                const file = input.files?.[0];
                if (!file) return;
        
                const reader = new FileReader();
                reader.onload = () => {
                    const img = new Image();
                    img.onload = () => {
                    const maxWidth = 600; // 400px
                    const scale = Math.min(1, maxWidth / img.width);
                    const width = img.width * scale;
                    const height = img.height * scale;
        
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;
        
                    ctx.drawImage(img, 0, 0, width, height);
                    const resizedBase64 = canvas.toDataURL('image/jpeg', 0.9); // can be image/png if preferred
        
                    const quill = editorRef.current?.getEditor();
                    if (!quill) return;
        
                    setTimeout(() => {
                        const range = quill.getSelection(true) || { index: quill.getLength() };
                        try {
                        quill.insertEmbed(range.index, 'image', resizedBase64, 'user');
                        quill.setSelection(range.index + 1);
                        } catch (err) {
                        console.error('Quill insertEmbed failed:', err);
                        }
                    }, 0);
                    };
        
                    img.src = reader.result as string;
                };
        
                reader.readAsDataURL(file);
                };
            });
        
            clearInterval(interval);
            }
        }, 100);
        
        return () => clearInterval(interval);
    }, [editorRef]);
    
    useEffect(() => {
        const quill = editorRef.current?.getEditor?.();
        if (!quill) return;
        
        const editorElem = quill.root;
        
        const handlePaste = (e: ClipboardEvent) => {
            const text = e.clipboardData?.getData('text/plain');
            if (text && (text.startsWith("http://") || text.startsWith("https://"))) {
            e.preventDefault();
            const range = quill.getSelection(true);
            if (range) {
                quill.insertText(range.index, text, { link: text }, 'user');
                quill.setSelection(range.index + text.length);
            }
            }
        };
        
        editorElem.addEventListener('paste', handlePaste);
        
        // Cleanup on unmount or when editor changes
        return () => {
            editorElem.removeEventListener('paste', handlePaste);
        };
    }, [editorRef.current?.getEditor?.()]);
          
    useEffect(() => {
        let editorElem: HTMLElement | null = null;
        let quillInstance: any = null;
    
        const handleTextChange = () => {
            const quill = editorRef.current?.getEditor?.();
            if (!quill) return;
    
            const range = quill.getSelection(true);
            if (!range || range.index < 1) return;
    
            const cursorIndex = range.index;
            const textBefore = quill.getText(0, cursorIndex);
            const words = textBefore.trim().split(/\s/);
            const lastWord = words[words.length - 1];
    
            if (/^https?:\/\/\S+$/.test(lastWord)) {
                const start = cursorIndex - lastWord.length;
                setTimeout(() => {
                    // @ts-expect-error
                    quill.formatText({ index: start, length: lastWord.length }, { link: lastWord }, 'user');
                }, 0);
            }
        };
    
        const handleKeyDown = (e: KeyboardEvent) => {
            const quill = editorRef.current?.getEditor?.();
            if (!quill) return;
          
            const range = quill.getSelection();
            if (!range) return;
          
            //const fullText = quill.getText(); // Entire content as plain text
            const cursorIndex = range.index;
            let latestText = userSearchTextRef.current;
            
            //setUserSearchText(latestText);

 
            //console.log(e.key);
            
            if (e.key === "Backspace") {
                latestText = latestText.slice(0, -1);
                setUserSearchText(latestText);
            } else if (e.key === "@" || (latestText.includes("@") && /^[\w\d]$/.test(e.key))) {
                latestText += e.key;
                setUserSearchText(latestText);
            } else {
                latestText = "";
                setUserSearchText(latestText);
            }

            //console.log(e.key);
            //console.log(latestText);
            //console.log(userSearchTextRef.current);
            
            if (latestText.includes("@")) {
                const bounds = quill.getBounds(cursorIndex);
                const editorRect = quill.root.getBoundingClientRect();
                if (bounds){
                    const top = bounds.top + editorRect.top;
                    const left = bounds.left + editorRect.left;
                    handleShowUserMention(latestText, top, left);
                }
            } else {
                setUserMentionContextMenuParams({ _id: "", x: 0, y: 0, text: "" }); 
            }
        };
          

        const interval = setInterval(() => {
            const quill = editorRef.current?.getEditor?.();
            if (!quill) return;
    
            quillInstance = quill;
            editorElem = quill.root;
    
            // Attach listeners
            quill.on('text-change', handleTextChange);
            editorElem.addEventListener('keydown', handleKeyDown);
    
            clearInterval(interval);
        }, 100);
    
        return () => {
            clearInterval(interval);
            if (editorElem && quillInstance) {
                quillInstance.off('text-change', handleTextChange);
                editorElem.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [editorRef.current?.getEditor?.()]);
    

    const save = (e: React.MouseEvent<HTMLAnchorElement>) => {
   
        e.preventDefault();
        e.stopPropagation();

        if (props.mode === "add") { 
            let newComment: MComment = {
                _id: "",
                userId: "",
                created: new Date(),
                modified: new Date(),
                active: true,
                cardId: props.card._id,
                richText: richText
            }

            let socket = openSocket(dispatch, settingsState);
            if (socket) {
                socket.emit("saveComment", { 
                    comment: newComment,
                });
            }
            //dispatch(saveComment(newComment));
            //checkForAndSendChat(newComment);
            setRichText("");
        } else {
            if (props.closeHandler && props.comment) {
                let comment = { ...props.comment};
                comment.richText = richText;
                let socket = openSocket(dispatch, settingsState);
                if (socket) {
                    socket.emit("saveComment", { 
                        comment: comment,
                    });
                }
                //dispatch(saveComment(comment));
                //checkForAndSendChat(comment);
                props.closeHandler(e, props.comment._id);
            }
        }

        toggleEdit(e, false);
    }

    const toggleEdit = (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement>, visible: boolean) => {
        e.preventDefault();
        e.stopPropagation();
        setEditing(visible);
    }

    const handleShowUserMention = (text: string, top: number, left: number) => {

        //setUserMentionContextMenuParams({ _id: "1", x: left, y: top, text: text });
        const container = document.querySelector('.cardContextMenu') as HTMLElement;
        const quill = editorRef.current?.getEditor();
        if (!container || !quill) return;
    
        const containerRect = container.getBoundingClientRect();
        const editorRect = quill.root.getBoundingClientRect();
        const cursorIndex = quill.getSelection()?.index ?? 0;
        const bounds = quill.getBounds(cursorIndex);
    
        if (bounds) {
            const relativeTop = bounds.top + editorRect.top - containerRect.top + container.scrollTop - 10;
            const relativeLeft = bounds.left + editorRect.left - containerRect.left + container.scrollLeft;
            setUserMentionContextMenuParams({
                _id: "1",
                x: relativeLeft,
                y: relativeTop,
                text: text
            });
        }
    }

    let project = projectState.projects.find(p => p.loaded);
    let existingComment = (props.comment) ? props.comment.richText : "";

    return (
        <>
            {editing ?
                <>
                    <div className="descriptionBox">
                        <div className="commentDetails" onClick={(e) => {e.preventDefault(); e.stopPropagation(); }}> 
                            <ReactQuill theme="snow"
                                ref={editorRef}
                                value={richText}
                                onChange={setRichText}
                                modules={{
                                    toolbar: {
                                      container: [...[
                                        { 'header': '1' },
                                        { 'header': '2' },
                                        "underline",
                                        "bold",
                                        "italic",
                                        "link",
                                        { 'list': 'ordered' },
                                        { 'list': 'bullet' },
                                        { 'indent': '-1' },
                                        { 'indent': '+1' }, 
                                        "image",
                                        "video",
                                        "code-block",
                                        'blockquote',
                                      ]],
                                    },
                                  }}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "30px" }}>
                        <Link to="" onClick={(e) => save(e)} className="btn-orange">
                            Save
                        </Link>
                        <Link to="" onClick={(e) => {
                            if (props.mode === "add") {
                                toggleEdit(e, false); 
                            } else {
                                if (props.closeHandler && props.comment) {
                                    props.closeHandler(e, props.comment._id);
                                }
                            }
                        }}
                            className="ps-3" style={{ fontWeight: "500" }}>
                            Cancel
                        </Link>
                    </div>
                </>
            :
                <>
                {project?.permission?.toString() !== "observer" ?
                    <div onClick={(e) => {toggleEdit(e, true)}} className="mt-2 mb-1">
                        <div style={{
                            width: "100%", padding: "7px 12px", borderRadius: "5px", boxShadow: "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
                            fontWeight: "500", fontSize: "11pt", color: "gray" }}>
                            Add a comment
                        </div>
                    </div>
                :
                    <>
                    {props.mode !== "add" &&
                        <>
                            {project?.permission?.toString() !== "observer" ?
                                <div onClick={(e) => {toggleEdit(e, true)}} 
                                className="cardDescription pt-3 ql-editor" 
                                style={{ height: "auto", paddingLeft: "0px" }}
                                dangerouslySetInnerHTML={{
                                    __html: (richText === '') ? existingComment : richText
                                }} />
                            :
                                <div className="cardDescription pt-3 ql-editor" 
                                style={{ height: "auto", paddingLeft: "0px" }}
                                dangerouslySetInnerHTML={{
                                    __html: (richText === '') ? existingComment : richText
                                }} />
                            }
                        </>
                    }
                    </>
                }
                </>
            }
             <UserMentionContextMenu params={userMentionContextMenuParams} />
        </>
    );    
}

export default CardComment;